import { useEffect, useState, useCallback } from "react";
import { trackEvent } from "@/lib/audit";
import { useCleanMode, useKeyboardOpen } from "@/lib/uiOverlays";
import { useCleanScreen } from "@/lib/pr28a/cleanScreenMode";

/**
 * Centralized mobile overlay manager. Single source of truth for whether
 * floating overlays (FABs, banners, CTAs, polls, ads) should render. It
 * combines:
 *   - Goal Clean Mode (auto-suppress during decisive moments)
 *   - Mobile keyboard open (typing → free chat area)
 *   - User Focus Mode preferences (per-channel toggles persisted locally)
 */

export type OverlayChannel =
  | "ads"
  | "polls"
  | "community"
  | "ai"
  | "rewards"
  | "betting"
  | "help"
  | "halftime"
  | "retention"
  | "share"
  | "ctas";

const FOCUS_PREFS_KEY = "vs:focus_mode_prefs_v1";
const FOCUS_ACTIVE_KEY = "vs:focus_mode_active_v1";

function readPrefs(): Record<OverlayChannel, boolean> {
  const defaults: Record<OverlayChannel, boolean> = {
    ads: true, polls: true, community: true, ai: true, rewards: true,
    betting: true, help: true, halftime: true, retention: true, share: true, ctas: true,
  };
  if (typeof window === "undefined") return defaults;
  try {
    const raw = window.localStorage.getItem(FOCUS_PREFS_KEY);
    if (!raw) return defaults;
    return { ...defaults, ...JSON.parse(raw) };
  } catch { return defaults; }
}

function writePrefs(prefs: Record<OverlayChannel, boolean>) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(FOCUS_PREFS_KEY, JSON.stringify(prefs));
}

export function useFocusModePrefs() {
  const [prefs, setPrefs] = useState<Record<OverlayChannel, boolean>>(readPrefs);
  const [focusActive, setFocusActive] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(FOCUS_ACTIVE_KEY) === "1";
  });

  const toggleChannel = useCallback((ch: OverlayChannel) => {
    setPrefs((p) => {
      const next = { ...p, [ch]: !p[ch] };
      writePrefs(next);
      void trackEvent("focus_mode_toggled", { channel: ch, enabled: next[ch] });
      return next;
    });
  }, []);

  const toggleFocusMode = useCallback(() => {
    setFocusActive((v) => {
      const next = !v;
      if (typeof window !== "undefined") {
        window.localStorage.setItem(FOCUS_ACTIVE_KEY, next ? "1" : "0");
      }
      void trackEvent("focus_mode_toggled", { active: next });
      return next;
    });
  }, []);

  return { prefs, focusActive, toggleChannel, toggleFocusMode };
}

/**
 * useOverlayVisible — single hook that every floating overlay should call.
 * Returns false when:
 *   - Goal Clean Mode is active (decisive moment)
 *   - Mobile keyboard is open (user typing)
 *   - Focus Mode is active and the channel is muted by user preference
 */
export function useOverlayVisible(channel: OverlayChannel): boolean {
  const cleanMode = useCleanMode();
  const keyboardOpen = useKeyboardOpen();
  const { active: cleanScreen } = useCleanScreen();

  // PR-28A: monetization & strategic channels can never be permanently
  // suppressed by the user. Only system-level conditions hide them:
  //   - Goal Clean Mode (decisive on-pitch moments)
  //   - Keyboard open (typing)
  //   - Temporary "Pantalla Limpia" timer
  const visible = !cleanMode && !keyboardOpen && !cleanScreen;

  useEffect(() => {
    void trackEvent("mobile_overlay_state_changed", {
      channel, visible, cleanMode, keyboardOpen, cleanScreen,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return visible;
}